import api from './client';

/** Un champ insérable : `code` est le placeholder écrit dans le document. */
export interface CatalogField {
  label: string;
  /** `%s` y est remplacé par l'intitulé saisi quand `ask` vaut true. */
  code: string;
  /** Champ libre : demander son intitulé avant de l'insérer. */
  ask?: boolean;
}

export interface CatalogGroup {
  group: string;
  fields: CatalogField[];
}

/** Un paragraphe éditable du document. `i` identifie le paragraphe d'origine. */
export interface TemplateBlock {
  i: number;
  text: string;
}

/** Un champ que l'opérateur renseignera au moment de générer le document. */
export interface SaisieField {
  key: string;
  label: string;
  type: 'text' | 'date';
}

export interface ParseResult {
  staging_id: string;
  original_name: string;
  blocks: TemplateBlock[];
}

export interface BuildResult {
  ok: boolean;
  relative_path: string;
  fields: SaisieField[];
  filename: string;
  size: string;
  modified: string;
}

export async function fetchFieldCatalog(): Promise<CatalogGroup[]> {
  const { data } = await api.get('documents/builder/catalog/');
  return data.catalog ?? [];
}

/** Téléverse un .docx et récupère ses paragraphes éditables. */
export async function parseTemplate(file: File): Promise<ParseResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('documents/builder/parse/', formData);
  return data;
}

/** Recharge un modèle déjà enregistré pour le modifier. */
export async function editTemplate(relativePath: string): Promise<ParseResult> {
  const { data } = await api.get('documents/builder/edit/', {
    params: { relative_path: relativePath },
  });
  return data;
}

export async function buildTemplate(payload: {
  staging_id: string;
  name: string;
  category: string;
  blocks: TemplateBlock[];
  overwrite?: boolean;
}): Promise<BuildResult> {
  const { data } = await api.post('documents/builder/build/', payload);
  return data;
}

/**
 * Champs à renseigner à la génération pour un modèle donné.
 *
 * Renvoie une liste vide plutôt que d'échouer : un modèle sans marqueur de
 * saisie est parfaitement valide, et la génération ne doit pas être bloquée
 * si cette lecture échoue.
 */
export async function fetchTemplateFields(relativePath: string): Promise<SaisieField[]> {
  try {
    const { data } = await api.get('documents/builder/fields/', {
      params: { relative_path: relativePath },
    });
    return data.fields ?? [];
  } catch {
    return [];
  }
}
